import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarClock, Trash2 } from 'lucide-react';
import { Panel, Button, EmptyState } from '../ui';

const ScheduleReportsPanel = ({
  templates = [],
  schedules = [],
  onCreate,
  onCancel,
  canManage = false,
  isCreating = false,
}) => {
  const [reportType, setReportType] = useState(templates[0]?.id || 'executive-summary');
  const [frequency, setFrequency] = useState('weekly');
  const [format, setFormat] = useState('pdf');
  const [recipients, setRecipients] = useState('');

  if (!canManage) return null;

  const handleCreate = async () => {
    try {
      await onCreate({
        reportType,
        frequency,
        format,
        recipients: recipients
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
        filters: {},
      });
      setRecipients('');
      toast.success('Report scheduled');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to schedule report');
    }
  };

  return (
    <Panel data-testid="schedule-reports-panel">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary-600" />
        Scheduled Reports
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <select
          className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          data-testid="schedule-report-type"
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
        <select
          className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          <option value="pdf">PDF</option>
          <option value="excel">Excel</option>
          <option value="csv">CSV</option>
        </select>
        <input
          type="text"
          placeholder="Recipients (comma-separated)"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          className="px-3 py-2 border border-secondary-300 rounded-md text-sm"
          data-testid="schedule-recipients"
        />
      </div>

      <Button
        variant="primary"
        onClick={handleCreate}
        disabled={isCreating || !reportType}
        data-testid="schedule-create-btn"
      >
        {isCreating ? 'Scheduling…' : 'Schedule Report'}
      </Button>

      <div className="mt-6 space-y-2">
        {schedules.length === 0 ? (
          <EmptyState
            title="No scheduled reports"
            description="Create a schedule to receive automated exports."
          />
        ) : (
          schedules.map((schedule) => (
            <div
              key={schedule.id}
              className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
              data-testid={`schedule-row-${schedule.id}`}
            >
              <div>
                <div className="font-medium text-secondary-900">{schedule.report_type}</div>
                <div className="text-sm text-secondary-600">
                  {schedule.frequency} • {schedule.format}
                </div>
              </div>
              <button
                type="button"
                className="text-red-600 hover:text-red-800 p-2"
                onClick={() => onCancel(schedule.id)}
                aria-label="Cancel schedule"
                data-testid={`cancel-schedule-${schedule.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
};

export default ScheduleReportsPanel;
